import React, { useEffect, useState, useRef } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { MetricStackTitle, ShinyBlockOuter, ShinyBody, ShinyHeading, PageWrapper, BasicText, Loading } from '../Styles';
import { MetricStack } from '../TestingLayout';
import { Tabular, TabularColumn } from '../core/Tabular';
import _ from 'lodash';
import { ColorBlock, DiversionHistogram } from '../Things';
import Metrics, { Metric } from '../core/Metrics';
import moment from 'moment';
import { Contributors, NewTabular } from './SustainabilityDashboard';
import Util from '../core/Util';

const styles = StyleSheet.create({
    containerStyle: {
        flexDirection: "column",
        flexBasis: "49%",
        justifyContent: "space-evenly",
        borderBottomLeftRadius: 5,
        borderBottomRightRadius: 5,
        marginTop: 5,
        marginRight: 5,
        marginBottom: 10,
        marginLeft: 5
    }
});

const Diversion = ({portalData}) => {
    const [data, setData] = useState([]);
    const pageSize = useRef(10);

    useEffect(()=>{
        const newData = _.reverse(_.sortBy(portalData.metrics.diversion, "metricValue"));

        let displayedRank = 1;
        let theIndex = 1;
        let previousValue = 0;
        _.forEach(newData, (siteDiversionMetric)=> {
            if(Math.abs(siteDiversionMetric.metricValue - previousValue) > 0.0001){
                displayedRank = theIndex;
                previousValue = siteDiversionMetric.metricValue;
            }
            
            siteDiversionMetric.rank = displayedRank;

            theIndex++;
        });

        setData(newData);
    },[portalData]);

    const isMobile = Util.isMobile();

    return <ShinyBlockOuter minHeight={400} flexBasis={isMobile? "95%" : "49%"}>
        <ShinyHeading>
            <MetricStackTitle>Landfill Diversion ({portalData.dateRange.length || 0} Months)</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <Tabular data={data} pageSize={pageSize.current}>
                <TabularColumn key="rank" data={(r) => r.rank} title="Rank" width={isMobile? null : 50}/>
                <TabularColumn key="name" data={(r) => r.title} title="Site" width={isMobile? null : 300}/>
                <TabularColumn key="name" data={(r) => Util.roundNice(r.related.comparedValue, 2)} title="Landfill (MT)" width={isMobile? null : 70}/>
                <TabularColumn key="metric_tonnes_diverted" data={(r) => Util.roundNice(r.related.metricValue, 2)} title="Diverted (MT)" width={isMobile? null : 70}/>
                <TabularColumn key="diversion_percent" data={(r) => Util.roundNice(r.metricValue, 2)} title="%" width={isMobile? null : 70}/>
            </Tabular>
        </ShinyBody>
    </ShinyBlockOuter>;
}

const DiversionChart = ({portalData}) => {    
    return <ShinyBlockOuter flexBasis={"95%"}>
        <ShinyHeading>
            <MetricStackTitle>{"Historical Diversion"}</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <DiversionHistogram data={ portalData.diversion } defaultDataPoint={_.last(_.filter(portalData.diversion, (d)=>d.waste + d.recycled > 0))} maxValue={20} portalData={portalData}/>                        
        </ShinyBody>
    </ShinyBlockOuter>
}

const Composition12Month = ({portalData}) => {
    const [details, setDetails] = useState({cols: [], data: []});

    useEffect(()=>{
        if( !_.isEmpty(portalData) ){
            let monthlyCols = [];
            const materialColumn = <TabularColumn 
                key="material" 
                data={(row) =>{
                    return <View style={{flexDirection: "row", alignItems: "center"}}>
                        <ColorBlock color={row.main_colour} style={{marginHorizontal: 5}}/>
                        <BasicText>{row.name}</BasicText>
                    </View>
                }}
                title="Material class (MT)" 
                className="material-col"
            />;

            monthlyCols.push(materialColumn);
        
            const groups = _.groupBy(portalData.materials, (r)=>r.name);

            const monthlyWaste = _.map( groups, (group)=>{
                const row = _.pick( group[0], "name", "accent_colour", "main_colour", "density", "material_type" );
                
                _.each( group, (g)=>{
                    if(row[g.date] == undefined){
                        row[ g.date ] = g.metric_tonnes;
                    } else {
                        row[ g.date ] += g.metric_tonnes;
                    }
                });
                return row;
            });

            _.each( portalData.dateRange, (m, idx)=>{
                const month = moment(m);
                const niceMonth = month.format('MMM YY');
                const className = niceMonth == moment().format('MMM YY') ? "current-month" : "";
                let highlight = false;

                if(niceMonth == moment().format('MMM YY')){
                    highlight = true;
                }

                const columnStyle = {};

                if(highlight == true){
                    columnStyle.backgroundColor = "rgba(203, 203, 203, 0.5)";
                }

                monthlyCols.push( <TabularColumn key={"month-" + month + "|" + idx} data={ (row)=>Util.roundNice(row[month.format("YYYY-MM")], 2) } title={niceMonth} className={className} style={columnStyle} /> );
            });
    
            setDetails({cols: monthlyCols, data: monthlyWaste});
        }
    }, [portalData]);

    return <ShinyBlockOuter flexBasis={"95%"}>
        <ShinyHeading>
            <MetricStackTitle>Composition History</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <ScrollView horizontal={true} contentContainerStyle={{flexDirection: "row", flexGrow: 1}}>
                <NewTabular data={details.data}>
                    {details.cols}
                </NewTabular>
            </ScrollView>
        </ShinyBody>
    </ShinyBlockOuter>;
}

const EnvironmentalImpactDashboard = ({portalData}) =>{

    if (portalData.materials == null){
        return <PageWrapper></PageWrapper>;
    }

    let metricOrderedOptions = ["compositionImpact", "diversion", "destinationImpact"];
    
    let metricData = {};
    if(portalData.metrics != undefined){
        if(_.findIndex(portalData.metrics.sustainability, {category: "destinationImpact"}) < 0){
            metricOrderedOptions = ["compositionImpact", "diversion", "recycling"];
        }
        
        metricData = _.groupBy(_.orderBy(_.filter(portalData.metrics["sustainability"], (m)=>metricOrderedOptions.indexOf(m.category) > -1), "periodLength", "desc"), "category");
    }

    return <PageWrapper>
        <Metrics data={metricData} categoryOrder={metricOrderedOptions}/>
        <DiversionChart portalData={portalData} />
        <Diversion portalData={portalData} />
        <Contributors portalData={portalData} title="Waste & Recycling Streams"/>
        <Composition12Month portalData={portalData} />
    </PageWrapper>
};

export default EnvironmentalImpactDashboard;